// The feature-request form's pure half: its six fields and their copy, what it refuses to
// send and why, the exact body POST /api/feature-requests is sent, and how a refusal from
// the server lands on the form. RequestForm.jsx renders and sequences; every decision the
// form makes is here, where node --test can reach it (the repo has no DOM harness).
//
// It MIRRORS the server rather than replacing it. The authority is CREATE_BODY in
// src/routes/api/feature-requests.js: the same required fields, the same 200-character
// title, the same 10,000-character problem, proposal and rules (PROSE_MAX), the same
// refusal of control and text-direction characters in the title, and the same trimming
// before any of it is judged. Mirroring buys an answer without a round trip;
// it does not make this a gate, and whatever the server still refuses is shown on the form
// by `submitFailure`.
//
// THE LABELS ARE ONE LIST. The board's Details panel names the same fields, and reads
// FIELD_LABELS from here rather than retyping them.
//
// THE OPTIONS ARE THE BOARD'S LISTS. web/src never imports from src/, so the area and impact
// lists are board.js's copies (which test/web/feature-board.test.js pins to the server's),
// not a third copy here.
//
// WHAT IS SENT IS WHAT WAS TYPED. Text fields go out exactly as typed — not trimmed, not
// escaped, not stripped of markup. The server trims; nothing escapes on the way in because
// React escapes on the way out (the board renders every field as text, never as markup).

import { UNSAFE_TEXT } from '../create/newContactForm.js';
import { AREAS, IMPACTS, IMPACT_LABELS } from './board.js';

/** The server's limit on a title, after trimming (CREATE_BODY's maxLength). */
export const TITLE_MAX = 200;

/**
 * The server's limit on each of the problem, proposal and rules, after trimming
 * (src/lib/feature-request-vocab.js PROSE_MAX; test/web/feature-request-form.test.js pins
 * the two equal).
 */
export const PROSE_MAX = 10_000;

/**
 * How long the form waits for POST /api/feature-requests before it gives up. apiFetch has
 * no timeout of its own, and while a filing is in flight the form refuses to close over a
 * page that is inert behind it, so a request that never answered would hold the whole app.
 */
export const FILE_TIMEOUT_MS = 30_000;

/**
 * The six fields, in the order the form draws them, with the spec's copy. `label` names
 * the field wherever it is shown (on the form, `formLabel` adds "(optional)"); `tip` is
 * the hint under the input, or null for none. `kind` says what to draw.
 */
export const FIELDS = Object.freeze([
  { name: 'title', label: 'Title', required: true, tip: null, kind: 'line' },
  { name: 'problem', label: 'What’s the problem today?', required: true, tip: null, kind: 'text' },
  { name: 'proposal', label: 'How should it work?', required: true, tip: 'The ideal steps, from the user’s side', kind: 'text' },
  { name: 'rules', label: 'Rules / logic', required: false, tip: 'Who can do it, what’s locked, edge cases', kind: 'text' },
  { name: 'area', label: 'Where should this be added?', required: true, tip: null, kind: 'choice' },
  { name: 'impact', label: 'How much would it help?', required: true, tip: null, kind: 'choice' },
].map((field) => Object.freeze(field)));

/** Each field's label by name: what the board's Details panel heads each answer with. */
export const FIELD_LABELS = Object.freeze(
  Object.fromEntries(FIELDS.map((field) => [field.name, field.label])),
);

/** A field's label on the form: the optional one says so. */
export function formLabel(field) {
  return field.required ? field.label : `${field.label} (optional)`;
}

/** Where a request belongs: the board's areas. The stored value is the label. */
export const AREA_OPTIONS = Object.freeze(
  AREAS.map((area) => Object.freeze({ value: area, label: area })),
);

/** How much it would help, least to most, in the board's words. */
export const IMPACT_OPTIONS = Object.freeze(
  IMPACTS.map((impact) => Object.freeze({ value: impact, label: IMPACT_LABELS[impact] })),
);

const FIELD_NAMES = FIELDS.map((field) => field.name);

/** What each required field says when it is left empty, or a list choice is not a choice. */
const MISSING = {
  title: 'Give the request a title.',
  problem: 'Say what the problem is today.',
  proposal: 'Say how it should work.',
  area: 'Choose where this should be added.',
  impact: 'Choose how much it would help.',
};

/** How each prose field is named in the sentence that says it is too long. */
const PROSE_NAMES = { problem: 'the problem', proposal: 'the proposal', rules: 'the rules' };

/** 10000 as "10,000", the way a person reads a limit. */
const count = (n) => n.toLocaleString('en-US');

/** A blank form: no text, nothing chosen. A fresh object each time. */
export function emptyValues() {
  return { title: '', problem: '', proposal: '', rules: '', area: '', impact: '' };
}

const text = (value) => (typeof value === 'string' ? value : '');

/**
 * True when closing the form would throw away something the person put in it: text in any
 * field (whitespace alone is not text), or a choice made in either list.
 */
export function hasText(values) {
  return FIELD_NAMES.some((name) => text(values?.[name]).trim() !== '');
}

/**
 * Turns the form's values into the body for POST /api/feature-requests, or into the
 * reasons it cannot be sent.
 *
 * Returns `{ problems, body, focus }`:
 *   - `problems` is `[{ field, message }]`, in field order, empty when the body is usable;
 *   - `body` is null whenever there are problems;
 *   - `focus` is the first field with a problem, where the caret should go, or null.
 *
 * The body holds exactly title, problem, proposal, area and impact, plus `rules` only when
 * it holds text — absent otherwise, never "" or null. Every judgement is made on the
 * trimmed value, as the server makes it; the value sent is the one typed.
 */
export function buildRequestBody(values) {
  const raw = {};
  for (const name of FIELD_NAMES) raw[name] = text(values?.[name]);
  const trimmed = Object.fromEntries(FIELD_NAMES.map((name) => [name, raw[name].trim()]));

  const problems = [];
  if (trimmed.title === '') {
    problems.push({ field: 'title', message: MISSING.title });
  } else if (trimmed.title.length > TITLE_MAX) {
    problems.push({
      field: 'title',
      message: `Keep the title to ${TITLE_MAX} characters; it has ${trimmed.title.length}.`,
    });
  } else if (UNSAFE_TEXT.test(trimmed.title)) {
    problems.push({
      field: 'title',
      message: 'The title holds a character that is not allowed (a tab, control or text-direction character).',
    });
  }
  for (const name of ['problem', 'proposal', 'rules']) {
    if (name !== 'rules' && trimmed[name] === '') {
      problems.push({ field: name, message: MISSING[name] });
    } else if (trimmed[name].length > PROSE_MAX) {
      problems.push({
        field: name,
        message: `Keep ${PROSE_NAMES[name]} to ${count(PROSE_MAX)} characters; it has ${count(trimmed[name].length)}.`,
      });
    }
  }
  if (!AREAS.includes(raw.area)) problems.push({ field: 'area', message: MISSING.area });
  if (!IMPACTS.includes(raw.impact)) problems.push({ field: 'impact', message: MISSING.impact });

  if (problems.length) return { problems, body: null, focus: problems[0].field };

  const body = {
    title: raw.title,
    problem: raw.problem,
    proposal: raw.proposal,
    area: raw.area,
    impact: raw.impact,
  };
  if (trimmed.rules !== '') body.rules = raw.rules;
  return { problems, body, focus: null };
}

/** One message per field, the first one given for it winning. */
export function fieldErrors(problems) {
  const out = {};
  for (const { field, message } of problems ?? []) {
    if (!(field in out)) out[field] = message;
  }
  return out;
}

/**
 * True when a failed POST is a REFUSAL: the server answered with a 4xx, so the request was
 * not filed. Anything else leaves the outcome unknown: no answer at all (a network
 * failure, the timeout), or a 5xx, which a proxy can send while the server carries on and
 * which the server itself can send after the row is in.
 */
function isRefusal(err) {
  return Number.isInteger(err?.status) && err.status >= 400 && err.status < 500;
}

/**
 * What a failed POST says, and where.
 *
 * A REFUSAL (a 4xx) says the request was not filed, led by the server's own message —
 * for a 403 that is the right sentence whichever 403 it was (a rank or licence refusal,
 * or a stale CSRF token that says to reload). When a 422 names fields
 * (`details.fields`), each one the form draws gets the server's word under it; any other
 * is named in the message, so no refusal is dropped.
 *
 * ANY OTHER FAILURE has an unknown outcome: the POST may have committed. It says the
 * request MAY not have been filed and to check the board before filing it again, and
 * `outcomeUnknown` tells the caller to have the board load its list again, so a request
 * that did land is there to be seen.
 *
 * @returns {{message: string, fieldErrors: Record<string, string>, outcomeUnknown: boolean}}
 */
export function submitFailure(err) {
  if (!isRefusal(err)) {
    const reason = err?.name === 'TimeoutError'
      ? `no answer came back within ${FILE_TIMEOUT_MS / 1000} seconds`
      // Its own full stop dropped, since the sentence goes on.
      : (err?.message || '').replace(/[.\s]+$/, '') || 'no answer came back';
    return {
      message: `Your request may not have been filed: ${reason}. Check the board for it before filing it again.`,
      fieldErrors: {},
      outcomeUnknown: true,
    };
  }

  const named = Array.isArray(err.details?.fields) ? err.details.fields : [];
  const onForm = named.filter((p) => FIELD_NAMES.includes(p?.field));
  const offForm = named.filter((p) => !FIELD_NAMES.includes(p?.field));

  let message = `Your request was not filed: ${err.message || 'something went wrong.'}`;
  if (offForm.length) {
    message += ` (${offForm.map((p) => `${p?.field}: ${p?.message}`).join('; ')})`;
  }
  return { message, fieldErrors: fieldErrors(onForm), outcomeUnknown: false };
}

/**
 * The new request out of POST /api/feature-requests's answer, or a throw that
 * `submitFailure` reads as an unknown outcome. apiFetch answers null for a 2xx that is not
 * JSON (something upstream of Express answered), and a 2xx without a row in `data` is no
 * better: either way the request may well have been filed, and nothing says what it was.
 */
export function createdFrom(answer) {
  const created = answer?.data;
  if (created === null || typeof created !== 'object' || Array.isArray(created)) {
    throw new Error('The server’s answer was not understood.');
  }
  return created;
}

/**
 * What the form's status region says while a filing is in flight: that the press of
 * "File request" registered, and — once a close has been tried — why nothing closed.
 * Once the server answers the form can be closed whichever way it went, so this promises
 * no more than that.
 */
export function filingStatus({ sending, closeRefused }) {
  if (!sending) return '';
  if (closeRefused) {
    return `Still filing… You can close this once the server answers, within ${FILE_TIMEOUT_MS / 1000} seconds.`;
  }
  return 'Filing…';
}

/**
 * What a close (Escape, the backdrop, ✕, Cancel or Close) does:
 *
 *   'refuse'  a filing is in flight: nothing closes (closing would not stop the POST);
 *   'keep'    the last filing's outcome is unknown: it closes WITHOUT asking and what was
 *             typed is kept, because the person has to close the form to check the board
 *             for the request, and a question that ends in "what you typed will be lost"
 *             would make them choose between checking and keeping their text;
 *   'ask'     the form holds something: confirm before discarding it;
 *   'close'   the form is empty: it just closes.
 */
export function closeAction({ sending, outcomeUnknown, values }) {
  if (sending) return 'refuse';
  if (outcomeUnknown && hasText(values)) return 'keep';
  return hasText(values) ? 'ask' : 'close';
}

/** Said under an unknown outcome, so the person knows closing to check the board is safe. */
export const KEPT_NOTE = 'Closing keeps what you typed: “New request” opens it again.';
